'use client';
import React, { useCallback } from 'react';
import { When } from 'react-if';

import { gql } from '@apollo/client';
import { add, differenceInDays, parseISO } from 'date-fns';

import { CenteredContainer } from '@nerdwallet/currency/components/CenteredContainer';
import { Box } from '@nerdwallet/currency/components/Box';
import { Button } from '@nerdwallet/currency/components/Button';
import { Link } from '@nerdwallet/currency/components/Link';
import { LoadingSpinner } from '@nerdwallet/currency/components/LoadingSpinner';
import { Text } from '@nerdwallet/currency/components/Text';
import { Title } from '@nerdwallet/currency/dist/components/Title/Title';
import { transformToDate } from '@nerdwallet/utilities/dist/date-utils';
import { useTuVisibilityContext } from '@components/transunion-flow/tu-visibility-context';
import { TuStep } from '@components/transunion-flow/tu-modal-stepper';
import {
  CreditProfileAuthStatus,
  CreditProfileAuthThinFileReason,
} from '@generated-gql/generated-types';

import WebDeprecationBanner from '@components/web-deprecation-banner';
import { useQueryWithLog } from '@lib/hooks/logging-hooks';
import { getLogger } from '@lib/logging';
import { SegmentPageView } from '@lib/segment';
import styles from './credit-score-unactivated.module.scss';

const GET_UNACTIVATED_CREDIT_SCORE = gql`
  query getUnactivatedCreditScore {
    tuAuthenticationStatusProfile {
      blockedAt
      thinFileReason
      isDeceased
      hasThinFile
      notEnoughData
    }
  }
`;

interface Props {
  status: CreditProfileAuthStatus;
}

const formatBlockedUntilDate = (blockedUntil) => {
  const parsedBlockedUntilDate = transformToDate(blockedUntil);
  const days = Math.abs(differenceInDays(new Date(), parsedBlockedUntilDate));

  if (days >= 2) {
    return `in ${days} days`;
  }

  if (days === 1) {
    return `tomorrow`;
  }

  return null;
};

const logger = getLogger('CreditScoreUnactivated');

const CreditScoreUnactivated: React.FC<Props> = ({ status }) => {
  const { data, error, loading } = useQueryWithLog(
    logger,
    GET_UNACTIVATED_CREDIT_SCORE,
    {
      skip:
        status === CreditProfileAuthStatus.Inactive ||
        status === CreditProfileAuthStatus.Deactivated,
    },
  );

  const { blockedAt, thinFileReason, isDeceased, hasThinFile, notEnoughData } =
    data?.tuAuthenticationStatusProfile ?? {};
  const { setTuVisibility } = useTuVisibilityContext();

  const showCreditFlowModal = useCallback(() => {
    setTuVisibility({
      show: true,
      tuStep: TuStep.TU_INITIAL,
    });
  }, [setTuVisibility]);

  const EMPTY_STATE_MESSAGES_CONFIG = {
    BLOCKED: ({ blockedUntil }) => ({
      title: 'Hm. We couldn’t get your credit score.',
      subtitle: `We weren’t able to fetch your credit report, but you can try again ${blockedUntil}.`,
    }),
    PENDING: {
      title: 'You’re just steps away from your free credit score.',
      subtitle:
        'We need a few more details from you before we can grab your credit report.',
      buttonText: 'Get your free credit score',
      ctaFunction: showCreditFlowModal,
    },
    DECEASED: {
      title:
        'According to the credit bureau, the owner of this social security number is deceased.',
      subtitle: (
        <div>
          To correct this error, please{' '}
          <Link
            href="https://www.transunion.com/credit-disputes/dispute-your-credit"
            target="_blank"
            rel="noopener noreferrer"
          >
            file a dispute with TransUnion.
          </Link>
        </div>
      ),
    },
    NOT_ENOUGH_DATA: {
      title:
        'Looks like you don’t have enough credit history to display a credit report.',
      subtitle: (
        <div>
          No worries. You can turn that around.{' '}
          <Link
            href="https://www.nerdwallet.com/blog/finance/how-to-build-credit/?trk_origin=mynerdwallet"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn how to build your credit.
          </Link>
        </div>
      ),
    },
    NO_DATA: {
      title: 'Looks like you’re just starting out.',
      subtitle:
        'We weren’t able to get your credit report because you don’t have enough credit history. But don’t worry, you’ll get there — check out our tips on building credit.',
      buttonText: 'Read article',
      ctaFunction: () =>
        window.open(
          'https://www.nerdwallet.com/blog/finance/how-to-build-credit/?trk_origin=mynerdwallet',
          '_blank',
        ),
    },
    DUPLICATE: {
      title:
        "We couldn't get your score last time. Try again if 30 days have passed.",
      subtitle: (
        <div>
          If your last attempt was more recent, we suggest waiting a bit. If you
          try again too soon, TransUnion may issue another temporary block.
          <br />
          <br />
          <Link
            href="https://support.nerdwallet.com/hc/en-us/articles/6639316347533"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more about why this happens.
          </Link>
        </div>
      ),
      buttonText: 'Try again',
      ctaFunction: showCreditFlowModal,
    },
    FAILED_VERIFICATION: {
      title: 'Hm. Looks like there was a typo.',
      subtitle:
        'We weren’t able to fetch your credit report, probably due to a small typo. Don’t worry, it happens to the best of us — let’s give it another shot.',
      buttonText: 'Try again',
      ctaFunction: showCreditFlowModal,
    },
    UNACTIVATED: {
      title: 'You’re just steps away from your free credit score.',
      subtitle:
        "Free, unlimited access to your credit score and report. Checking is secure, private, and won't affect your score.",
      buttonText: 'Get your free credit score',
      ctaFunction: showCreditFlowModal,
    },
  };

  const getEmptyStateMessages = () => {
    if (status === CreditProfileAuthStatus.Blocked && blockedAt) {
      const blockedUntil = formatBlockedUntilDate(
        add(parseISO(blockedAt), { months: 1 }),
      );
      if (blockedUntil)
        return EMPTY_STATE_MESSAGES_CONFIG.BLOCKED({ blockedUntil });
    }

    if (thinFileReason === CreditProfileAuthThinFileReason.Duplicate) {
      return EMPTY_STATE_MESSAGES_CONFIG.DUPLICATE;
    }

    if (
      [
        CreditProfileAuthThinFileReason.NoData,
        CreditProfileAuthThinFileReason.Other,
      ].includes(thinFileReason)
    ) {
      return EMPTY_STATE_MESSAGES_CONFIG.NO_DATA;
    }

    if (thinFileReason === CreditProfileAuthThinFileReason.FailVerification) {
      return EMPTY_STATE_MESSAGES_CONFIG.FAILED_VERIFICATION;
    }

    if (status === CreditProfileAuthStatus.Pending) {
      return EMPTY_STATE_MESSAGES_CONFIG.PENDING;
    }

    if (isDeceased) {
      return EMPTY_STATE_MESSAGES_CONFIG.DECEASED;
    }

    if (notEnoughData || hasThinFile) {
      return EMPTY_STATE_MESSAGES_CONFIG.NOT_ENOUGH_DATA;
    }

    return EMPTY_STATE_MESSAGES_CONFIG.UNACTIVATED;
  };

  const emptyStateMessages = getEmptyStateMessages();
  const { title, subtitle } = emptyStateMessages;
  const buttonText =
    'buttonText' in emptyStateMessages
      ? (emptyStateMessages.buttonText as string)
      : undefined;
  const ctaFunction =
    emptyStateMessages && 'ctaFunction' in emptyStateMessages
      ? () => {
          (emptyStateMessages as { ctaFunction: () => void }).ctaFunction();
        }
      : () => {};

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return null;
  }

  return (
    <SegmentPageView name="credit_score_unactivated">
      <WebDeprecationBanner />
      <CenteredContainer>
        <Box className={styles.image}>
          <Box className={styles.textContainer}>
            <Title>{title}</Title>
            <Text>{subtitle}</Text>
            <When condition={!!buttonText}>
              <Button primary onClick={ctaFunction}>
                {buttonText}
              </Button>
            </When>
          </Box>
        </Box>
      </CenteredContainer>
    </SegmentPageView>
  );
};

export default CreditScoreUnactivated;
